/**
 * Pure dirty-check helpers for workspace autosave / reconnect.
 * Compare live state against the last saved baseline — not against the version API snapshot.
 */

type LampLike = {
  id?: string;
  uuid: string;
  name: string;
  position: [number, number, number] | number[];
  productId?: number;
  variantId?: number;
  seriesId?: number;
  manufacturerId?: number;
  rotationRadians?: number;
  marketMatchGroup?: string;
};

type ModelLayerLike = {
  uuid: string;
  visible?: boolean;
  [key: string]: unknown;
};

/** True when lamp scene fields differ, matched by uuid (order-independent). */
export function lampsHaveChanged(lamps: LampLike[], previousLamps: LampLike[]): boolean {
  if (lamps.length !== previousLamps.length) return true;

  const prevByUuid = new Map(previousLamps.map((lamp) => [lamp.uuid, lamp]));
  for (const lamp of lamps) {
    const prevLamp = prevByUuid.get(lamp.uuid);
    if (!prevLamp) return true;
    if (
      lamp.name !== prevLamp.name ||
      lamp.position[0] !== prevLamp.position[0] ||
      lamp.position[1] !== prevLamp.position[1] ||
      lamp.position[2] !== prevLamp.position[2] ||
      lamp.productId !== prevLamp.productId ||
      lamp.variantId !== prevLamp.variantId ||
      lamp.seriesId !== prevLamp.seriesId ||
      lamp.manufacturerId !== prevLamp.manufacturerId ||
      lamp.rotationRadians !== prevLamp.rotationRadians ||
      lamp.marketMatchGroup !== prevLamp.marketMatchGroup
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Persistable layer fields only — omits ephemeral UI state like `visible`
 * so toggling layer visibility does not look like an unsaved scene edit.
 */
export function modelLayersHaveChanged(modelLayers: ModelLayerLike[], previousModelLayers: ModelLayerLike[]): boolean {
  return (
    JSON.stringify(normalizeModelLayersForDirtyCheck(modelLayers)) !==
    JSON.stringify(normalizeModelLayersForDirtyCheck(previousModelLayers))
  );
}

export function normalizeModelLayersForDirtyCheck(modelLayers: ModelLayerLike[]) {
  return modelLayers
    .map(({ visible: _visible, ...persistable }) => persistable)
    .sort((a, b) => String(a.uuid).localeCompare(String(b.uuid)));
}

export function meshGroupsHaveChanged(meshGroups: unknown[], previousMeshGroups: unknown[]): boolean {
  return JSON.stringify(meshGroups) !== JSON.stringify(previousMeshGroups);
}
